import { useCallback, useRef, useState } from 'react';
import { validateBrazilAddress, type BrazilAddressFields } from '~/utils/brazilAddress';

export type JourneyAddressErrors = Partial<Record<keyof BrazilAddressFields, string>>;

/** Endereço que o CEP preenche (`GET ZipCodes`, por exemplo). */
export type JourneyZipcodeLookup = (
  zipcode: string
) => Promise<Pick<BrazilAddressFields, 'street' | 'neighborhood' | 'city' | 'state'>>;

const digitsOf = (value: string) => value.replace(/\D/g, '');

const formatZipcode = (value: string) => {
  const digits = digitsOf(value ?? '');
  return digits.length === 8 ? digits.replace(/^(\d{5})(\d{3})$/, '$1-$2') : value ?? '';
};

/** Formulário com o CEP já mascarado e os campos vazios no lugar de `null`. */
export function toJourneyAddressForm(data: Partial<BrazilAddressFields>): BrazilAddressFields {
  return {
    zipcode: formatZipcode(data.zipcode ?? ''),
    street: data.street ?? '',
    number: data.number ?? '',
    neighborhood: data.neighborhood ?? '',
    additional_details: data.additional_details ?? '',
    state: data.state ?? '',
    city: data.city ?? ''
  };
}

/**
 * Estado de um formulário de endereço brasileiro: CEP com autopreenchimento (só consulta quando chega a 8
 * dígitos novos), erros por campo e validação. Usado no endereço do equipamento e no de cobrança.
 */
export function useJourneyAddressForm(initial: Partial<BrazilAddressFields>, lookupZipcode: JourneyZipcodeLookup) {
  const [form, setForm] = useState<BrazilAddressFields>(() => toJourneyAddressForm(initial));
  const [errors, setErrors] = useState<JourneyAddressErrors>({});
  const lastLookup = useRef(digitsOf(initial.zipcode ?? ''));

  const reset = useCallback((data: Partial<BrazilAddressFields>) => {
    setForm(toJourneyAddressForm(data));
    setErrors({});
    lastLookup.current = digitsOf(data.zipcode ?? '');
  }, []);

  const change = useCallback((field: keyof BrazilAddressFields, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  }, []);

  const changeZipcode = useCallback(
    async (value: string) => {
      change('zipcode', value);
      const digits = digitsOf(value);
      if (digits.length !== 8 || digits === lastLookup.current) return;
      lastLookup.current = digits;
      try {
        const found = await lookupZipcode(value);
        setForm((current) => ({ ...current, ...found }));
        setErrors((current) => ({
          ...current,
          street: undefined,
          neighborhood: undefined,
          state: undefined,
          city: undefined
        }));
      } catch {
        setErrors((current) => ({ ...current, zipcode: 'CEP não encontrado.' }));
      }
    },
    [change, lookupZipcode]
  );

  /** Valida e acende os erros; mantém o «CEP não encontrado.» da consulta. Devolve `true` quando está ok. */
  const validate = useCallback(() => {
    const found: JourneyAddressErrors = validateBrazilAddress(form);
    if (errors.zipcode) found.zipcode = errors.zipcode;
    setErrors(found);
    return Object.keys(found).length === 0;
  }, [errors.zipcode, form]);

  return { form, errors, change, changeZipcode, reset, validate };
}
